import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { KIDS_BELTS, ADULT_BELTS } from '../lib/constants'

export default function InvitePage() {
  const { clubSlug } = useParams()
  const navigate = useNavigate()
  const [club, setClub] = useState(null)
  const [mode, setMode] = useState('signup')
  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '',
    type: 'adult', belt_index: 0, country: 'HR',
    phone: '', date_of_birth: '', parent_email: ''
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const [existingFighter, setExistingFighter] = useState(null)
  const [transferMode, setTransferMode] = useState(false)
  const [transferSent, setTransferSent] = useState(false)

  useEffect(() => {
    async function loadClub() {
      const { data } = await supabase.from('clubs').select('*').eq('slug', clubSlug).single()
      setClub(data)
      setLoading(false)
    }
    loadClub()
  }, [clubSlug])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const belts = form.type === 'kids' ? KIDS_BELTS : ADULT_BELTS

  async function handleLogin(e) {
    e.preventDefault()
    setSaving(true); setError('')
    const { data, error: authErr } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password })
    if (authErr) { setError(authErr.message); setSaving(false); return }

    const { data: prof } = await supabase.from('profiles').select('*, fighters(*)').eq('id', data.user.id).single()
    if (prof?.fighter_id && prof.fighters) {
      if (prof.fighters.club_id === club.id) { navigate('/my-card'); return }
      setExistingFighter(prof.fighters)
      setTransferMode(true)
      setSaving(false)
      return
    }
    navigate('/my-card')
    setSaving(false)
  }

  async function handleSignup(e) {
    e.preventDefault()
    if (!form.email || !form.password || !form.first_name || !form.last_name) {
      setError('Please fill in all required fields'); return
    }
    if (form.type === 'adult' && !form.phone) { setError('Phone number is required'); return }
    if (!form.date_of_birth) { setError('Date of birth is required'); return }
    setSaving(true); setError('')

    const { data, error: authErr } = await supabase.auth.signUp({ email: form.email, password: form.password })
    if (authErr) { setError(authErr.message); setSaving(false); return }

    const { data: fighter } = await supabase.from('fighters').insert({
      club_id: club.id,
      first_name: form.first_name,
      last_name: form.last_name,
      type: form.type,
      belt_index: parseInt(form.belt_index),
      sessions_count: 0,
      belt_sessions: 0,
      country: form.country,
      avatar_style: 'avatar',
      avatar_seed: Math.floor(Math.random() * 100),
      pending_approval: true,
      self_registered: true,
      email: form.email,
      phone: form.phone || null,
      date_of_birth: form.date_of_birth || null,
    }).select().single()

    if (fighter) {
      await supabase.from('profiles').insert({
        id: data.user.id,
        club_id: club.id,
        role: 'athlete',
        full_name: form.first_name + ' ' + form.last_name,
        fighter_id: fighter.id,
      })
      await supabase.from('fighter_gym_history').insert({
        fighter_id: fighter.id, club_id: club.id, is_current: true
      })
      // Seed skills
      const { data: techs } = await supabase.from('techniques').select('id')
      if (techs?.length) {
        await supabase.from('fighter_skills').insert(techs.map(t => ({ fighter_id: fighter.id, technique_id: t.id, stars: 0 })))
      }
    }

    setSaving(false)
    setDone(true)
  }

  async function requestTransfer() {
    setSaving(true)
    await supabase.from('gym_transfers').insert({
      fighter_id: existingFighter.id,
      from_club_id: existingFighter.club_id,
      to_club_id: club.id,
    })
    setSaving(false)
    setTransferSent(true)
  }

  if (loading) return <Screen club={null}><p style={{ color: '#888', textAlign: 'center' }}>Loading...</p></Screen>
  if (!club) return <Screen club={null}><p style={{ color: '#c00', textAlign: 'center' }}>Club not found.</p></Screen>

  if (done) return (
    <Screen club={club}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
        <div style={{ color: '#fff', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Request sent!</div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
          Your profile has been created and is waiting for your coach to verify your belt rank.
          You'll be able to log in once approved.
        </div>
        <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 10, padding: '12px', fontSize: 12, color: 'rgba(255,255,255,0.5)', textAlign: 'left' }}>
          <div style={{ marginBottom: 4 }}>📌 Your submitted belt: <strong style={{ color: '#fff' }}>{belts[form.belt_index]?.n}</strong></div>
          <div>⏳ Coach will verify and confirm your rank before you can check in.</div>
        </div>
      </div>
    </Screen>
  )

  if (transferSent) return (
    <Screen club={club}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>📨</div>
        <div style={{ color: '#fff', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Transfer request sent!</div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
          {club.name} has been notified. Once approved your full record will transfer.
        </div>
      </div>
    </Screen>
  )

  if (transferMode && existingFighter) return (
    <Screen club={club}>
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>🥋</div>
        <div style={{ color: '#fff', fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Welcome back, {existingFighter.first_name}!</div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, lineHeight: 1.6 }}>
          We found your existing fighter record. Transfer it to {club.name}?
        </div>
      </div>
      <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem', marginBottom: '1.25rem', border: '1px solid rgba(255,255,255,0.12)' }}>
        <div style={{ color: '#fff', fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{existingFighter.first_name} {existingFighter.last_name}</div>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{existingFighter.sessions_count || 0} total sessions · full history carries over</div>
      </div>
      <button onClick={requestTransfer} disabled={saving} style={{ ...btnStyle, width: '100%', marginBottom: 10 }}>
        {saving ? 'Sending...' : `Transfer to ${club.name}`}
      </button>
      <button onClick={() => navigate('/my-card')} style={{ ...btnSecStyle, width: '100%' }}>Stay at current gym</button>
    </Screen>
  )

  return (
    <Screen club={club}>
      <div style={{ display: 'flex', background: 'rgba(0,0,0,0.25)', borderRadius: 10, padding: 3, marginBottom: '1.5rem' }}>
        {[['signup', 'Join the club'], ['login', 'I have an account']].map(([m, label]) => (
          <button key={m} onClick={() => { setMode(m); setError('') }} style={{
            flex: 1, padding: '8px', border: 'none', borderRadius: 8, cursor: 'pointer',
            background: mode === m ? 'rgba(255,255,255,0.15)' : 'transparent',
            color: mode === m ? '#fff' : 'rgba(255,255,255,0.5)',
            fontWeight: mode === m ? 600 : 400, fontSize: 13, fontFamily: 'inherit'
          }}>{label}</button>
        ))}
      </div>

      <form onSubmit={mode === 'signup' ? handleSignup : handleLogin}>
        {mode === 'signup' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="First name" value={form.first_name} onChange={v => set('first_name', v)} placeholder="Ivan" />
              <Field label="Last name" value={form.last_name} onChange={v => set('last_name', v)} placeholder="Horvat" />
            </div>
            <Field label="Type">
              <select value={form.type} onChange={e => { set('type', e.target.value); set('belt_index', 0) }} style={selectStyle}>
                <option value="adult">Adult</option>
                <option value="kids">Kids</option>
              </select>
            </Field>
            <Field label="My current belt">
              <select value={form.belt_index} onChange={e => set('belt_index', e.target.value)} style={selectStyle}>
                {belts.map((b, i) => <option key={i} value={i}>{b.n}</option>)}
              </select>
            </Field>
            <div style={{ background: 'rgba(255,200,0,0.08)', border: '1px solid rgba(255,200,0,0.2)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: 'rgba(255,220,100,0.8)', lineHeight: 1.6 }}>
              ⚠️ Your belt rank will be verified by your coach before you can check in to classes.
            </div>
            <Field label="Date of birth *" type="date" value={form.date_of_birth} onChange={v => set('date_of_birth', v)} />
            {form.type === 'adult' && (
              <Field label="Phone number *" type="tel" value={form.phone} onChange={v => set('phone', v)} placeholder="+385 91 234 5678" />
            )}
            {form.type === 'kids' && (
              <Field label="Parent email" type="email" value={form.parent_email} onChange={v => set('parent_email', v)} placeholder="parent@example.com" />
            )}
          </>
        )}
        <Field label="Email" type="email" value={form.email} onChange={v => set('email', v)} placeholder="ivan@example.com" />
        <Field label="Password" type="password" value={form.password} onChange={v => set('password', v)} placeholder="••••••••" />

        {error && <div style={{ color: '#ffaaaa', fontSize: 12, marginBottom: 12, padding: '8px 12px', background: 'rgba(255,0,0,0.15)', borderRadius: 8 }}>{error}</div>}

        <button type="submit" disabled={saving} style={{ ...btnStyle, width: '100%', marginTop: 4 }}>
          {saving ? 'Please wait...' : mode === 'signup' ? 'Request to join' : 'Log in'}
        </button>
      </form>
    </Screen>
  )
}

function Screen({ club, children }) {
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#0a1628 0%,#1a3a6a 50%,#0a1628 100%)', fontFamily: "'Segoe UI', system-ui, sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: 52, marginBottom: 10 }}>🥋</div>
          {club ? (
            <>
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, marginBottom: 4 }}>You've been invited to join</div>
              <div style={{ color: '#fff', fontSize: 24, fontWeight: 700 }}>{club.name}</div>
            </>
          ) : <div style={{ color: '#fff', fontSize: 22, fontWeight: 700 }}>MyAthleteCard</div>}
        </div>
        <div style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(12px)', borderRadius: 16, padding: '1.75rem', border: '1px solid rgba(255,255,255,0.12)' }}>
          {children}
        </div>
      </div>
    </div>
  )
}

function Field({ label, type = 'text', value, onChange, placeholder, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', color: 'rgba(255,255,255,0.65)', fontSize: 12, marginBottom: 5, fontWeight: 500 }}>{label}</label>
      {children || <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />}
    </div>
  )
}

const btnStyle = { padding: '12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#1860a8,#0c4480)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }
const btnSecStyle = { padding: '11px', borderRadius: 10, background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }
const selectStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 14, outline: 'none' }
